import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  BattleQuestion,
  ServerAnswerResolution
} from "../domain/battleMachine";
import type { Database, Json } from "../../../shared/types/database";

type RpcClient = SupabaseClient<Database>;

interface RemoteQuestion {
  id: string;
  slug: string;
  prompt: string;
  questionType: "single_choice" | "multiple_choice";
  difficulty: number;
  timeLimitSeconds: number;
  options: Array<{
    id: string;
    label: string;
  }>;
}

export interface BattleStartResult {
  battleSessionId: string;
  playerHp: number;
  playerMaxHp: number;
  opponentHp: number;
  opponentMaxHp: number;
  difficulty: number;
  heroLevel: number;
  critChance: number;
  attack: number;
  specialGain: number;
  campaignLevel: number;
  isReplay: boolean;
  enemyKey: string;
  enemyNameRu: string;
  enemyNameUz: string;
  enemyVariant: number;
  enemyTier: number;
  enemyAttackStyle: "melee" | "ranged" | "magic";
  isBoss: boolean;
}

export interface BattleReward {
  status: "victory" | "defeat" | "abandoned";
  xpEarned: number;
  coinsEarned: number;
  correctAnswers: number;
  totalAnswers: number;
  maxCombo: number;
  heroLevelBefore: number;
  heroLevelAfter: number;
  levelUp: boolean;
  experienceAfter: number;
  xpIntoLevel: number;
  xpForNextLevel: number;
  upgradePointsEarned: number;
  upgradePointsAvailable: number;
  totalCoins: number;
  currentStreak: number;
  bestStreak: number;
  campaignLevel: number;
  campaignLevelUnlocked: number;
  campaignCompleted: boolean;
  isReplay: boolean;
  firstCompletion: boolean;
}

export async function startBattleSession(
  client: RpcClient,
  heroId: string,
  subjectId: string,
  campaignLevel: number,
  locale: "ru" | "uz"
): Promise<BattleStartResult> {
  const { data, error } = await client.rpc("start_battle_session", {
    p_hero_id: heroId,
    p_subject_id: subjectId,
    p_campaign_level: campaignLevel,
    p_locale: locale
  });

  if (error) {
    throw error;
  }

  return parseBattleStart(data);
}

export async function loadBattleQuestions(
  client: RpcClient,
  battleSessionId: string,
  locale: "ru" | "uz"
): Promise<BattleQuestion[]> {
  const { data, error } = await client.rpc("get_battle_questions", {
    p_battle_session_id: battleSessionId,
    p_locale: locale
  });

  if (error) {
    throw error;
  }

  return parseBattleQuestions(data);
}

export async function submitBattleAnswer(
  client: RpcClient,
  battleSessionId: string,
  questionId: string,
  questionIndex: number,
  selectedOptionIds: string[],
  responseTimeMs: number,
  useSpecial: boolean
): Promise<ServerAnswerResolution> {
  const { data, error } = await client.rpc("submit_battle_answer", {
    p_battle_session_id: battleSessionId,
    p_question_id: questionId,
    p_question_index: questionIndex,
    p_selected_option_ids: selectedOptionIds,
    p_response_time_ms: responseTimeMs,
    p_use_special: useSpecial
  });

  if (error) {
    throw error;
  }

  return parseAnswerResolution(data);
}

export async function finishBattleSession(
  client: RpcClient,
  battleSessionId: string,
  reason: "terminal" | "timeout" | "abandoned"
): Promise<BattleReward> {
  const { data, error } = await client.rpc("finish_battle_session", {
    p_battle_session_id: battleSessionId,
    p_reason: reason
  });

  if (error) {
    throw error;
  }

  return parseBattleReward(data);
}

export function parseBattleQuestions(data: Json): BattleQuestion[] {
  if (!Array.isArray(data)) {
    throw new Error("Battle question deck must be an array.");
  }

  const questions = data.map(parseRemoteQuestion);
  if (questions.length === 0) {
    throw new Error("Battle question deck is empty.");
  }

  return questions.map(toBattleQuestion);
}

function parseRemoteQuestion(value: Json): RemoteQuestion {
  if (!isRecord(value)
    || typeof value.id !== "string"
    || typeof value.slug !== "string"
    || typeof value.prompt !== "string"
    || typeof value.questionType !== "string"
    || !["single_choice", "multiple_choice"].includes(value.questionType)
    || typeof value.difficulty !== "number"
    || typeof value.timeLimitSeconds !== "number"
    || !Array.isArray(value.options)
  ) {
    throw new Error("Battle question payload is invalid.");
  }

  const options = value.options.map((option) => {
    if (!isRecord(option)
      || typeof option.id !== "string"
      || typeof option.label !== "string"
    ) {
      throw new Error("Battle answer option payload is invalid.");
    }

    return {
      id: option.id,
      label: option.label
    };
  });

  if (options.length < 2) {
    throw new Error("Battle question must have at least two options.");
  }

  return {
    id: value.id,
    slug: value.slug,
    prompt: value.prompt,
    questionType: value.questionType as RemoteQuestion["questionType"],
    difficulty: value.difficulty,
    timeLimitSeconds: value.timeLimitSeconds,
    options
  };
}

function toBattleQuestion(question: RemoteQuestion): BattleQuestion {
  const match = /^(addition|subtraction|multiplication)-(\d+)-(\d+)$/.exec(question.slug);

  if (!match) {
    return {
      id: question.id,
      slug: question.slug,
      prompt: [question.prompt],
      spokenPrompt: question.prompt,
      questionType: question.questionType,
      difficulty: question.difficulty,
      timeLimitSeconds: question.timeLimitSeconds,
      options: question.options
    };
  }

  const operation = match[1];
  const left = Number(match[2]);
  const right = Number(match[3]);
  const operator = operation === "multiplication"
    ? "×"
    : operation === "subtraction"
      ? "−"
      : "+";
  return {
    id: question.id,
    slug: question.slug,
    prompt: [String(left), operator, String(right), "=", "?"],
    spokenPrompt: question.prompt,
    questionType: question.questionType,
    difficulty: question.difficulty,
    timeLimitSeconds: question.timeLimitSeconds,
    options: question.options
  };
}

function parseAnswerResolution(data: Json): ServerAnswerResolution {
  if (!isRecord(data)
    || typeof data.isCorrect !== "boolean"
    || typeof data.isCritical !== "boolean"
    || typeof data.isSpecial !== "boolean"
    || typeof data.damageDealt !== "number"
    || typeof data.damageReceived !== "number"
    || typeof data.playerHp !== "number"
    || typeof data.opponentHp !== "number"
    || typeof data.status !== "string"
    || typeof data.combo !== "number"
    || typeof data.maxCombo !== "number"
    || typeof data.specialMeter !== "number"
    || typeof data.correctAnswers !== "number"
    || typeof data.totalAnswers !== "number"
    || !["active", "victory", "defeat"].includes(data.status)
  ) {
    throw new Error("Battle answer result is invalid.");
  }

  return {
    isCorrect: data.isCorrect,
    isCritical: data.isCritical,
    isSpecial: data.isSpecial,
    damageDealt: data.damageDealt,
    damageReceived: data.damageReceived,
    playerHp: data.playerHp,
    opponentHp: data.opponentHp,
    status: data.status as ServerAnswerResolution["status"],
    combo: data.combo,
    maxCombo: data.maxCombo,
    specialMeter: data.specialMeter,
    correctAnswers: data.correctAnswers,
    totalAnswers: data.totalAnswers
  };
}

function parseBattleStart(data: Json): BattleStartResult {
  const fields = [
    "playerHp", "playerMaxHp", "opponentHp", "opponentMaxHp",
    "difficulty", "heroLevel", "critChance", "attack", "specialGain",
    "campaignLevel", "enemyVariant", "enemyTier"
  ] as const;
  if (!isRecord(data)
    || typeof data.battleSessionId !== "string"
    || typeof data.isReplay !== "boolean"
    || typeof data.enemyKey !== "string"
    || typeof data.enemyNameRu !== "string"
    || typeof data.enemyNameUz !== "string"
    || typeof data.enemyAttackStyle !== "string"
    || !["melee", "ranged", "magic"].includes(data.enemyAttackStyle)
    || typeof data.isBoss !== "boolean"
    || fields.some((field) => typeof data[field] !== "number")
  ) {
    throw new Error("Battle start result is invalid.");
  }

  return data as unknown as BattleStartResult;
}

function parseBattleReward(data: Json): BattleReward {
  const numericFields = [
    "xpEarned", "coinsEarned", "correctAnswers", "totalAnswers", "maxCombo",
    "heroLevelBefore", "heroLevelAfter", "experienceAfter", "xpIntoLevel",
    "xpForNextLevel", "upgradePointsEarned", "upgradePointsAvailable",
    "totalCoins", "currentStreak", "bestStreak", "campaignLevel",
    "campaignLevelUnlocked"
  ] as const;
  if (!isRecord(data)
    || typeof data.status !== "string"
    || !["victory", "defeat", "abandoned"].includes(data.status)
    || typeof data.levelUp !== "boolean"
    || typeof data.campaignCompleted !== "boolean"
    || typeof data.isReplay !== "boolean"
    || typeof data.firstCompletion !== "boolean"
    || numericFields.some((field) => typeof data[field] !== "number")
  ) {
    throw new Error("Battle reward result is invalid.");
  }

  return data as unknown as BattleReward;
}

function isRecord(value: Json): value is { [key: string]: Json } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
